'use client';

import { createContext, ReactNode, useContext, useMemo } from 'react';

import kebabCase from 'lodash/kebabCase';

import { ClientSafeConfig } from '@/lib/types/tenant';

export type { ClientSafeConfig } from '@/lib/types/tenant';

interface ConfigContextType {
  config: ClientSafeConfig;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
  config: ClientSafeConfig;
}

function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc, key) => (acc as Record<string, unknown>)?.[key], obj);
}

export function ConfigProvider({ children, config }: ConfigProviderProps) {
  const value = useMemo(() => ({ config }), [config]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ClientSafeConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
}

export function useConfigValue<T = unknown>(keyPath: string): T | null {
  const context = useContext(ConfigContext);
  const config = context?.config;
  return useMemo(
    () => (config ? ((getByPath(config, keyPath) ?? null) as T | null) : null),
    [config, keyPath],
  );
}

export function useTenantKey(): string {
  return useConfig().tenantKey;
}

/** The kebab-cased tenant name used as the backend path segment (`/{tenantName}/...`). */
export function useTenantName(): string {
  const tenantKey = useTenantKey();
  return useMemo(() => kebabCase(tenantKey), [tenantKey]);
}
